import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';

export interface TradeCard {
  id: string;
  cardId: number;
  name: string;
  set: string;
  setCode: string;
  condition: string;
  language: string;
  foil: boolean;
  edition: string;
  imageUrl: string;
  marketPrice: number;
  pointValue: number;
  foilPointValue?: number;
  nonFoilPointValue?: number;
  quantity: number;
  wantListId: number;
  acceptableSets?: string[];  // Sets the receiver will accept
  availableInFoil?: boolean;  // Whether this card is available in foil
  availableInNonFoil?: boolean;  // Whether this card is available in non-foil
}

export interface ShippingAddress {
  firstName?: string;
  lastName?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

export interface TradePackage {
  userId: number;
  username: string;
  avatarUrl?: string;
  pointBalance: number;
  cards: TradeCard[];
  totalValue: number;
  shippingAddress?: ShippingAddress;
  isLocked?: boolean;
  lockedBy?: string;
  lockExpiresAt?: string;
}

export interface AvailablePackagesResponse {
  packages: TradePackage[];
  totalPackages: number;
  hasMore: boolean;
}

export interface AvailablePackagesRequest {
  limit?: number;
  offset?: number;
}

export interface InitiateTradeRequest {
  receiverId: number;
  lockToken?: string;  // Lock token required by backend
  cards: {
    wantListId: number;
    cardId?: string;  // Specific card ID for exact version matching
    quantity: number;
    condition: string;
    language: string;
    foil: boolean;
    edition?: string;
    set?: string;  // The specific set the sender is sending
  }[];
}

export interface InitiateTradeResponse {
  tradeId: string;
  success: boolean;
  message?: string;
}

export interface ActiveTrade {
  id: string;
  tradeNumber: string;
  traderId: number;
  traderName: string;
  traderAvatar?: string;
  initiated: string;
  shipped?: string;
  completed?: string;
  estimatedDelivery?: string;
  status: 'ready' | 'transit' | 'completed';
  items: {
    qty: number;
    name: string;
    set: string;
    condition: string;
    language: string;
    foil: boolean;
    image: string;
    pointValue?: number;
  }[];
  totalValue: number;
  tradeFee?: number;
  shippingAddress?: ShippingAddress;
}

export interface ActiveTradesResponse {
  trades: ActiveTrade[];
  totalTrades: number;
}

export interface TradeLockRequest {
  receiverId: number;
}

export interface TradeLockResponse {
  success: boolean;
  message?: string;
  lockId?: string;
  lockToken?: string;  // Token to validate lock ownership
  expiresAt?: string;
}

export interface TradeUnlockRequest {
  receiverId: number;
  lockToken: string;
}

export interface MarkAsShippedRequest {
  tradeId: string;
}

export interface MarkAsShippedResponse {
  success: boolean;
  message?: string;
  shippedAt?: string;
}

export interface RemoveCardFromTradeRequest {
  tradeId: string;
  itemIndex: number;  // The index of the item to remove from the trade
}

export interface RemoveCardFromTradeResponse {
  success: boolean;
  message?: string;
  updatedTrade?: ActiveTrade;
  refundedPoints?: number;
}

export interface CompleteTradeRequest {
  tradeId: string;
}

export interface CompleteTradeResponse {
  success: boolean;
  message?: string;
  completedAt?: string;
  pointsCredited?: number;
  tradeFee?: number;
  tradeFeePercentage?: number;
}

export interface AdminTrade {
  id: string;
  tradeNumber: string;
  senderId: number;
  senderUsername: string;
  senderAvatar?: string;
  receiverId: number;
  receiverUsername: string;
  receiverAvatar?: string;
  totalValue: number;
  initiatedAt: string;
  shippedAt?: string;
  completedAt?: string;
  status: 'ready' | 'transit' | 'completed';
  items: {
    name: string;
    set: string;
    quantity: number;
    pointValue: number;
  }[];
}

export interface AdminTradesRequest {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: 'ready' | 'transit' | 'completed';
}

export interface AdminTradesResponse {
  trades: AdminTrade[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface InboundTradeCountResponse {
  count: number;
  hasOverdueTrades: boolean;
}

// Auto-Match feature interfaces
export interface MatchIndexPackage {
  matchingCardIds: string[];  // Format: "{wantListId}-{cardId}"
  totalCards: number;
}

export interface MatchIndexResponse {
  packages: Record<string, MatchIndexPackage>;
  totalMatchingCards: number;
  totalMatchingPackages: number;
  generatedAt: string;
  error?: string;        // "DB_ERROR" | "TIMEOUT" when failed
  errorMessage?: string; // Human-readable error
}

export const tradeApi = createApi({
  reducerPath: 'tradeApi',
  baseQuery: baseQuery,
  tagTypes: ['TradePackages', 'ActiveTrades', 'InboundTradeCount', 'MatchIndex'],
  endpoints: (builder) => ({
    getAvailablePackages: builder.query<AvailablePackagesResponse, AvailablePackagesRequest>({
      query: ({ limit = 10, offset = 0 }) => ({
        url: `/trades/available-packages?limit=${limit}&offset=${offset}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['TradePackages'],
    }),
    
    initiateTrade: builder.mutation<InitiateTradeResponse, InitiateTradeRequest>({
      query: (body) => ({
        url: '/trades/initiate',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['TradePackages', 'ActiveTrades'],
    }),
    
    getActiveTrades: builder.query<ActiveTradesResponse, { type: 'sending' | 'receiving' }>({
      query: ({ type }) => ({
        url: `/trades/active?type=${type}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['ActiveTrades'],
    }),
    
    createTradeLock: builder.mutation<TradeLockResponse, TradeLockRequest>({
      query: ({ receiverId }) => ({
        url: `/trades/packages/${receiverId}/lock`,
        method: 'POST',
        body: {}, // Empty body - senderId is determined from auth token
        requiresAuth: true,
      }),
      invalidatesTags: ['TradePackages'],
    }),
    
    releaseTradeLock: builder.mutation<{ success: boolean }, TradeUnlockRequest>({
      query: ({ receiverId, lockToken }) => ({
        url: `/trades/packages/${receiverId}/lock`,
        method: 'DELETE',
        headers: {
          'X-Lock-Token': lockToken,  // Pass lock token as header
        },
        requiresAuth: true,
      }),
      invalidatesTags: ['TradePackages'],
    }),
    
    markAsShipped: builder.mutation<MarkAsShippedResponse, MarkAsShippedRequest>({
      query: (body) => ({
        url: '/trades/mark-shipped',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['ActiveTrades'],
    }),
    
    removeCardFromTrade: builder.mutation<RemoveCardFromTradeResponse, RemoveCardFromTradeRequest>({
      query: (body) => ({
        url: '/trades/remove-card',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['ActiveTrades', 'InboundTradeCount'],
    }),
    
    completeTrade: builder.mutation<CompleteTradeResponse, CompleteTradeRequest>({
      query: (body) => ({
        url: '/trades/complete',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['ActiveTrades', 'InboundTradeCount'],
    }),
    
    // Admin endpoint for getting all trades
    getAdminTrades: builder.query<AdminTradesResponse, AdminTradesRequest>({
      query: ({ page = 1, pageSize = 50, search, status }) => {
        const params = new URLSearchParams();
        params.append('page', page.toString());
        params.append('pageSize', pageSize.toString());
        if (search) params.append('search', search);
        if (status) params.append('status', status);

        return {
          url: `/admin/trades?${params.toString()}`,
          method: 'GET',
          requiresAuth: true,
        };
      },
      providesTags: ['ActiveTrades'],
    }),

    getInboundTradeCount: builder.query<InboundTradeCountResponse, void>({
      query: () => ({
        url: '/trades/inbound-count',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['InboundTradeCount'],
    }),

    // Auto-Match feature: Get match index for filtering packages
    getMatchIndex: builder.query<MatchIndexResponse, void>({
      query: () => ({
        url: '/trades/sendcards/match-index',
        method: 'GET',
        requiresAuth: true,
      }),
      keepUnusedDataFor: 120,  // 2 minutes - matches backend cache TTL
      providesTags: ['MatchIndex'],
    }),
  }),
});

export const {
  useGetAvailablePackagesQuery,
  useInitiateTradeMutation,
  useGetActiveTradesQuery,
  useCreateTradeLockMutation,
  useReleaseTradeLockMutation,
  useMarkAsShippedMutation,
  useRemoveCardFromTradeMutation,
  useCompleteTradeMutation,
  useGetAdminTradesQuery,
  useGetInboundTradeCountQuery,
  useGetMatchIndexQuery,
} = tradeApi;